import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { site } from '../config/site.js';
import EventCard from '../components/EventCard.jsx';
import Timeline from '../components/Timeline.jsx';
import { EVENT_TYPES, eventStatus } from '../lib/format.js';

const FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'live', label: 'Läuft gerade' },
  { key: 'community_day', label: EVENT_TYPES.community_day },
  { key: 'raid_day', label: EVENT_TYPES.raid_day },
  { key: 'raid_hour', label: EVENT_TYPES.raid_hour },
  { key: 'spotlight_hour', label: EVENT_TYPES.spotlight_hour },
];

/**
 * Marken an der Event-Zeitachse: R, wenn im Event ein lohnender Raid oder ein
 * starkes Pokémon steckt, P für den Trainerkampf. Die Zählung liefert die API
 * mit — die Liste selbst führt keine Pokémon.
 */
function eventMarken(e) {
  const liste = [];

  if (e.strongRaid > 0) {
    liste.push({
      kurz: 'R',
      titel: e.strongRaid + ' starke Pokémon für Raids',
      farbe: 'var(--verdict-strong)',
    });
  }
  if (e.strongPvp > 0) {
    liste.push({
      kurz: 'P',
      titel: e.strongPvp + ' davon stark im Trainerkampf',
      farbe: 'var(--accent-3)',
    });
  }

  return liste;
}

export default function EventsPage() {
  const [events, setEvents] = useState(null);
  const [entdeckungen, setEntdeckungen] = useState([]);
  const [heute, setHeute] = useState(0);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let active = true;
    api.events.list('current')
      .then((data) => {
        if (!active) return;
        setEvents(data.events);
        setEntdeckungen(data.entdeckungen || []);
        setHeute(data.heute || 0);
      })
      .catch((err) => active && setError(err.message));
    return () => { active = false; };
  }, []);

  const wochentage = {
    1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag',
    5: 'Freitag', 6: 'Samstag', 7: 'Sonntag',
  };

  const visible = (events ?? []).filter((event) => {
    if (filter === 'all') return true;
    if (filter === 'live') return eventStatus(event) === 'live';
    return event.eventType === filter;
  });

  const liveCount = (events ?? []).filter((e) => eventStatus(e) === 'live').length;

  return (
    <>
      <section className="hero">
        <span className="hero__tag">
          {liveCount > 0 ? `${liveCount} Event${liveCount === 1 ? '' : 's'} läuft gerade` : 'Aktuelle Events'}
        </span>
        <h1>{site.tagline}</h1>
        <p className="hero__sub">{site.description}</p>
      </section>

      {entdeckungen.length > 0 && (
        <section className="entdeckungen">
          <div className="spread">
            <h2 className="abschnitt">Tägliche Entdeckungen</h2>
            <span className="text-faint">{entdeckungen[0].saison}</span>
          </div>

          <div className="entdeckungen__woche">
            {entdeckungen.map((e) => (
              <article
                key={e.tag}
                className={e.tag === heute ? 'entdeckung entdeckung--heute' : 'entdeckung'}
              >
                <div className="entdeckung__tag">
                  {wochentage[e.tag]}
                  {e.tag === heute && <span className="entdeckung__marke">heute</span>}
                </div>

                <strong>{e.titel}</strong>

                <ul className="entdeckung__boni">
                  {e.boni.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </article>
            ))}
          </div>

          {entdeckungen[0].hinweis && (
            <p className="card__meta">{entdeckungen[0].hinweis}</p>
          )}
        </section>
      )}

      <div className="filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`chip${filter === f.key ? ' chip--on' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}

      {/* Die Achse zeigt bewusst ALLE geladenen Events, nicht die gefilterten:
          sie soll die zeitliche Einordnung geben, nicht die Filterauswahl
          spiegeln. */}
      {events && events.length > 0 && <Timeline events={events} marken={eventMarken} />}

      {!events && !error && (
        <div className="grid">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton" />)}
        </div>
      )}

      {events && visible.length === 0 && (
        <div className="empty">
          <div className="empty__icon">✧</div>
          <p><strong>Hier ist gerade nichts los.</strong></p>
          <p>Sobald im Adminbereich Events angelegt und veröffentlicht sind, tauchen sie hier auf.</p>
        </div>
      )}

      {visible.length > 0 && (
        <div className="grid">
          {visible.map((event) => <EventCard key={event.id} event={event} />)}
        </div>
      )}
    </>
  );
}
